import React, { useCallback, useMemo, useState } from 'react'
import { IconName } from '~components/Icon'
import { Input } from '~components/Form'
import { Dropdown, StyledDropdown } from '~components/Dropdown'
import { ComponentProps } from './Select.types'

export interface Props extends ComponentProps {
  selectedKey?: string
}

const Select: React.FC<Props> = ({
  onSelect,
  selectedKey,
  options,
  size,
  isDisabled,
  isError,
  zIndex,
  placeholder,
  className,
  placement = 'bottom-start',
  arrowPosition = 'left',
  variant = 'default',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const selectedOption = useMemo(() => options.find(o => o.key === selectedKey), [options, selectedKey])

  const inputField = useCallback(
    (disabled: boolean) => {
      const arrowIcon = isOpen ? IconName.chevron_up : IconName.chevron_down
      const itemIcon = selectedOption?.icon
      const value = selectedOption ? `${selectedOption.value}${selectedOption.caption ? ` - ${selectedOption.caption}` : ''}` : ''

      return (
        <Input
          readOnly={true}
          size={size}
          isError={isError}
          disabled={disabled}
          placeholder={placeholder}
          value={value}
          variant={variant}
          {...(arrowPosition === 'left' ? { icon: arrowIcon, iconRight: itemIcon } : { iconRight: arrowIcon, icon: itemIcon })}
        />
      )
    },
    [isOpen, size, isError, placeholder, selectedOption, arrowPosition, variant],
  )

  return isDisabled ? (
    inputField(true)
  ) : (
    <Dropdown shouldDismissOnClick={true} isOpen={isOpen} onOpenChange={setIsOpen} placement={placement} zIndex={zIndex}>
      {inputField(false)}

      <StyledDropdown.Content className={className}>
        {options.map(o => (
          <StyledDropdown.Option key={o.key} shouldWrap={!!o.caption} onClick={() => onSelect(o)}>
            {o.icon && <StyledDropdown.OptionIcon name={o.icon} />}
            {o.value}
            {o.caption && <StyledDropdown.OptionCaption>{o.caption}</StyledDropdown.OptionCaption>}
          </StyledDropdown.Option>
        ))}
      </StyledDropdown.Content>
    </Dropdown>
  )
}

Select.displayName = 'Select'
export default Select
